// src/components/Teacher/ClassCreationModal.tsx
import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useAuth } from '../../contexts/AuthContext';
 
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    secondary:        '#006947',
    secondaryContainer: '#d1fae5',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceHigh:      '#d6e3f9',
    surfaceLowest:    '#ffffff',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
    error:            '#b41340',
};
 
interface ClassCreationModalProps {
    onClose: () => void;
    onClassCreated: (classId: string) => void;
}
 
const ClassCreationModal: React.FC<ClassCreationModalProps> = ({ onClose, onClassCreated }) => {
    const { currentUser } = useAuth();
    const [className, setClassName] = useState('');
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState('');
    const [created, setCreated]     = useState<{ code: string; id: string } | null>(null);
 
    const generateJoinCode = (): string => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        let code = '';
        for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
        return code;
    };
 
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentUser) return;
        if (className.trim().length < 3) { setError('Class name must be at least 3 characters'); return; }
 
        setLoading(true);
        setError('');
 
        try {
            const joinCode = generateJoinCode();
            const docRef = await addDoc(collection(db, 'classes'), {
                name:              className.trim(),
                teacherId:         currentUser.uid,
                teacherName:       currentUser.displayName || 'Teacher',
                teacherEmail:      currentUser.email,
                joinCode,
                students:          {},
                customSuggestions: [],
                createdAt:         serverTimestamp(),
                archived:          false,
            });
            setCreated({ code: joinCode, id: docRef.id });
        } catch (err: any) {
            console.error('Error creating class:', err);
            setError(err.message || 'Failed to create class');
        } finally {
            setLoading(false);
        }
    };
 
    const handleDone = () => {
        if (created) onClassCreated(created.id);
        else onClose();
    };
 
    return (
        <div
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', backdropFilter: 'blur(4px)' }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{ background: C.surfaceLowest, borderRadius: '24px', padding: '36px', maxWidth: '480px', width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.18)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
                {created ? (
                    /* ── Success state ─────────────────────────────────────── */
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>🎉</div>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                            Class Created!
                        </h2>
                        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 28px 0', lineHeight: '1.6' }}>
                            Share this join code with your students so they can enroll.
                        </p>
 
                        {/* Join code display */}
                        <div style={{ background: C.primaryContainer, borderRadius: '16px', padding: '24px', marginBottom: '28px' }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: '800', color: C.primary, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '10px', fontFamily: "'Epilogue', sans-serif" }}>
                                Join Code
                            </div>
                            <div style={{ fontFamily: 'monospace', fontSize: '2.2rem', fontWeight: '900', color: C.primary, letterSpacing: '4px' }}>
                                {created.code}
                            </div>
                            <button
                                onClick={() => navigator.clipboard.writeText(created.code)}
                                style={{ marginTop: '12px', background: 'transparent', border: `1.5px solid ${C.primary}`, borderRadius: '9999px', padding: '6px 16px', fontSize: '0.78rem', fontWeight: '700', color: C.primary, cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.primary; }}
                            >
                                📋 Copy Code
                            </button>
                        </div>
 
                        <div style={{ background: C.secondaryContainer, borderRadius: '12px', padding: '12px 16px', marginBottom: '24px', fontSize: '0.82rem', color: C.secondary, fontWeight: '600', textAlign: 'left' }}>
                            💡 Students enter this code when signing up or from their Dashboard under "Join a Class".
                        </div>
 
                        <button
                            onClick={handleDone}
                            style={{ width: '100%', padding: '13px', background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: `0 4px 14px ${C.primary}40` }}
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    /* ── Creation form ─────────────────────────────────────── */
                    <>
                        <h2 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.5rem', fontWeight: '900', color: C.onBackground, margin: '0 0 8px 0' }}>
                            Create a New Class
                        </h2>
                        <p style={{ fontSize: '0.875rem', color: C.onSurfaceVariant, margin: '0 0 28px 0', lineHeight: '1.6' }}>
                            Give your class a name. Students will use a join code to enroll.
                        </p>
 
                        {error && (
                            <div style={{ background: '#fef2f2', border: `1.5px solid ${C.error}30`, borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: C.error, fontSize: '0.875rem', fontWeight: '600' }}>
                                {error}
                            </div>
                        )}
 
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '8px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.8rem', fontWeight: '700', color: C.onSurfaceVariant, fontFamily: "'Epilogue', sans-serif", textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Class Name
                                </label>
                                <input
                                    type="text"
                                    value={className}
                                    onChange={e => setClassName(e.target.value)}
                                    placeholder="e.g., Period 2 — Creative Writing"
                                    required
                                    style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${C.outlineVariant}`, borderRadius: '10px', fontSize: '0.95rem', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none', background: C.surfaceLowest, color: C.onSurface, transition: 'border-color 0.15s' }}
                                    onFocus={e => { e.target.style.borderColor = C.primary; }}
                                    onBlur={e  => { e.target.style.borderColor = C.outlineVariant; }}
                                />
                                <div style={{ fontSize: '0.78rem', color: C.onSurfaceVariant, marginTop: '6px' }}>
                                    e.g., "Period 2 — Creative Writing", "After School Club", "5th Grade English"
                                </div>
                            </div>
 
                            <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    disabled={loading}
                                    style={{ flex: 1, padding: '12px', background: C.surfaceLowest, color: C.onSurfaceVariant, border: `1.5px solid ${C.outlineVariant}`, borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = C.surfaceLow; } }}
                                    onMouseLeave={e => { e.currentTarget.style.background = C.surfaceLowest; }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={{ flex: 1, padding: '12px', background: loading ? C.outlineVariant : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`, color: '#fff', border: 'none', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Epilogue', sans-serif", boxShadow: loading ? 'none' : `0 4px 12px ${C.primary}40`, transition: 'opacity 0.15s' }}
                                    onMouseEnter={e => { if (!loading) e.currentTarget.style.opacity = '0.9'; }}
                                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                                >
                                    {loading ? 'Creating...' : 'Create Class'}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};
 
export default ClassCreationModal;